import * as React from 'react';
import MuiTabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import { useSearchParams } from 'react-router-dom';

interface TabLayoutProps {
  defaultTab?: number;
  onChange?: (newValue: number) => void;
  children: React.ReactElement<TabProps>[];
  ariaLabel?: string;
}

interface TabProps {
  name: string;
  title:
    | React.ReactNode
    | ((props: {
        value: number;
        index: number;
        ref?: React.Ref<HTMLDivElement>;
        onClick?: (event: React.MouseEvent) => void;
        children?: React.ReactNode;
        [key: string]: any;
      }) => React.ReactNode);
  children?: React.ReactNode;
  tabIsActive?: boolean;
}

interface CustomTabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
  ariaLabel?: string;
}

function CustomTabPanel(props: CustomTabPanelProps) {
  const { children, value, index, ariaLabel } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${ariaLabel}-tabpanel-${index}`}
      aria-labelledby={`${ariaLabel}-tabpanel-${index}`}
      style={{ marginTop: 20, padding: '0 4em' }}
    >
      {value === index && children}
    </div>
  );
}

function a11yProps(index: number, ariaLabel: string) {
  return {
    id: `${ariaLabel}-tab-${index}`,
    'aria-controls': `${ariaLabel}-tabpanel-${index}`,
  };
}

export const TabLayout: React.FC<TabLayoutProps> = ({
  defaultTab = 0,
  onChange,
  children,
  ariaLabel,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const numTabs = React.Children.count(children);
  // Get all tab names in order
  const tabNames = React.Children.map(children, (child) =>
    React.isValidElement(child) ? child.props.name : undefined,
  );
  // Find the index of the tab whose name matches the URL param
  const initialTab =
    tabParam && tabNames?.includes(tabParam)
      ? tabNames.indexOf(tabParam)
      : defaultTab;
  const [value, setValue] = React.useState(initialTab);

  React.useEffect(() => {
    // If the URL param changes externally, update the tab
    if (tabParam && tabNames?.includes(tabParam)) {
      const idx = tabNames.indexOf(tabParam);
      if (idx !== value) setValue(idx);
    }
  }, [tabParam, tabNames, value]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue >= 0 && newValue < numTabs) {
      setValue(newValue);
      onChange?.(newValue);
      // Set the tab param to the tab's name
      const newTabName = tabNames[newValue];
      setSearchParams({
        tab: newTabName,
      });
    }
  };

  return (
    <section
      style={{ marginLeft: 'auto', marginRight: 'auto', marginTop: '2em' }}
    >
      <MuiTabs
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        variant="fullWidth"
        sx={{ padding: '0 4em' }}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            return (
              <MuiTab
                {...a11yProps(index, ariaLabel || '')}
                label={
                  typeof child.props.title === 'function'
                    ? child.props.title({ value, index })
                    : child.props.title
                }
              />
            );
          }
          return null;
        })}
      </MuiTabs>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return (
            <CustomTabPanel value={value} index={index} ariaLabel={ariaLabel}>
              {React.cloneElement(child, { tabIsActive: value === index })}
            </CustomTabPanel>
          );
        }
        return null;
      })}
    </section>
  );
};

export const Tab: React.FC<TabProps> = ({
  title,
  children,
  name,
  tabIsActive,
}) => {
  return React.cloneElement(children as React.ReactElement<any>, {
    tabIsActive,
  });
};
